import React from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ExternalLink, Calendar, Edit2, Tag } from 'lucide-react';

const ViewTicketModal = ({
  isOpen,
  onClose,
  ticket,
  onEdit
}) => {
  if (!isOpen || !ticket) return null;

  const ticketName = ticket.name || 'Ticket Details';
  const confirmation = ticket.confirmation || ticket.pnr || '';
  const dayNum = ticket.day_number || ticket.dayNumber || (ticket.dayIdx !== undefined ? ticket.dayIdx + 1 : '');
  const assignedEvents = ticket.assigned_event_titles || [];

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white shrink-0">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                {dayNum ? `Day ${dayNum} Ticket` : 'Ticket'}
              </span>
              {confirmation && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-medium bg-[#067978]/10 text-[#067978] shrink-0">
                  #{confirmation}
                </span>
              )}
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight mt-0.5 break-words">
              {ticketName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto space-y-3.5 text-xs text-slate-700 flex-1">
          {/* Confirmation / PNR */}
          {confirmation && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Confirmation / PNR / Booking Ref
              </span>
              <div className="flex items-center gap-2 font-mono font-medium text-slate-800 text-xs">
                <Tag size={13} className="text-[#067978]" />
                <span>{confirmation}</span>
              </div>
            </div>
          )}

          {/* Assigned Events */}
          {assignedEvents.length > 0 && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Assigned To Events
              </span>
              <div className="flex items-center gap-2 font-medium text-slate-800 text-xs">
                <Calendar size={13} className="text-[#067978]" />
                <span className="truncate">{assignedEvents.join(', ')}</span>
              </div>
            </div>
          )}

          {/* Ticket File / Link */}
          {ticket.url ? (
            <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-100 space-y-2">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block">
                Attached Document
              </span>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-[#067978] shrink-0" />
                  <span className="text-xs text-slate-700 truncate font-medium">
                    {ticket.file_name || ticket.filename || 'Ticket Document'}
                  </span>
                </div>
                <a
                  href={ticket.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-2xs transition-colors shrink-0"
                >
                  <ExternalLink size={12} />
                  <span>Open Document</span>
                </a>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-100 text-slate-400 text-xs italic text-center">
              No external document or voucher file attached to this ticket.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200/70 rounded-lg transition-all cursor-pointer"
          >
            Close
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onEdit(ticket);
              }}
              className="px-3.5 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Edit2 size={12} />
              <span>Edit Ticket</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewTicketModal;
